/**
 * FlahaSOIL v2 — Project selector used by the soil-test wizard.
 *
 * Loads the user's projects through the active API client and exposes
 * them as a dropdown so the wizard never asks the user to type a raw
 * project id. A `+ Create project` shortcut opens the same
 * `NewProjectDialog` used by the Projects list page; the newly-created
 * project is preselected so the wizard can move on.
 */
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useRef, useState } from "react";
import type { ProjectSummaryDTO } from "@flaha/shared-types";

import { getApiClient } from "../../../services/apiClientProvider";
import { useAuth } from "../../../auth";
import {
	canCreateUserProjects,
	FREE_TIER_PROJECT_CODE,
	isFreePlan,
} from "../../plan/planSurface";
import { NewProjectDialog } from "../../projects/components/NewProjectDialog";
import type { EntitlementSnapshot } from "@flaha/shared-types";

interface ProjectSelectorProps {
	value: string | null;
	onChange: (projectId: string | null) => void;
	disabled?: boolean;
}

export function ProjectSelector(props: ProjectSelectorProps) {
	const { value, onChange, disabled } = props;
	const { status: sessionStatus, activeOrganization } = useAuth();
	const [projects, setProjects] = useState<ProjectSummaryDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [retryKey, setRetryKey] = useState(0);
	const [entitlements, setEntitlements] = useState<EntitlementSnapshot | null>(
		null
	);
	const previousOrganizationId = useRef(activeOrganization?.id);
	const freePlan = isFreePlan(entitlements);
	const allowNewProject = canCreateUserProjects(entitlements);

	useEffect(() => {
		if (sessionStatus !== "authenticated") return;
		let cancelled = false;
		void getApiClient()
			.getMyUsage()
			.then((res) => {
				if (!cancelled) setEntitlements(res.usage.entitlements);
			})
			.catch(() => {
				if (!cancelled) setEntitlements(null);
			});
		return () => {
			cancelled = true;
		};
	}, [sessionStatus, activeOrganization?.id]);

	useEffect(() => {
		if (sessionStatus !== "authenticated") return;
		let cancelled = false;
		setError(null);
		setProjects(null);
		void getApiClient()
			.listProjects({})
			.then((res) => {
				if (cancelled) return;
				setProjects(res.projects);
				// Free: auto-select the system Free Tier project.
				const freeSystem = res.projects.find(
					(p) => p.code === FREE_TIER_PROJECT_CODE
				);
				if (freeSystem && (freePlan || res.projects.length === 1)) {
					if (value !== freeSystem.id) {
						onChange(freeSystem.id);
					}
				} else if (
					value &&
					!res.projects.some((project) => project.id === value)
				) {
					onChange(null);
				}
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : String(err));
				}
			});
		return () => {
			cancelled = true;
		};
	}, [sessionStatus, activeOrganization?.id, retryKey, freePlan]);

	useEffect(() => {
		const nextOrganizationId = activeOrganization?.id;
		if (
			previousOrganizationId.current &&
			previousOrganizationId.current !== nextOrganizationId
		) {
			onChange(null);
		}
		previousOrganizationId.current = nextOrganizationId;
	}, [activeOrganization?.id, onChange]);

	const handleCreated = (projectId: string) => {
		setDialogOpen(false);
		setRetryKey((key) => key + 1);
		onChange(projectId);
	};

	if (projects === null && !error) {
		return (
			<Stack direction="row" spacing={1} alignItems="center" role="status" aria-live="polite">
				<CircularProgress size={16} />
				<Box>Loading projects…</Box>
			</Stack>
		);
	}

	return (
		<>
			{freePlan ? (
				<Alert severity="info" sx={{ mb: 1 }} data-testid="free-tier-project-hint">
					Free plan uses the system workspace <strong>Free Tier</strong>. Upgrade to
					Professional to create additional projects.
				</Alert>
			) : null}
			<Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
				<FormControl fullWidth required error={!!error}>
					<InputLabel id="wizard-project-label">Project</InputLabel>
					<Select
						labelId="wizard-project-label"
						label="Project"
						value={value ?? ""}
						onChange={(e) => onChange(String(e.target.value))}
						disabled={
							disabled ||
							(projects?.length ?? 0) === 0 ||
							freePlan
						}
					>
						{(projects ?? []).map((p) => (
							<MenuItem key={p.id} value={p.id}>
								{p.name}
								{p.code ? ` (${p.code})` : ""}
							</MenuItem>
						))}
					</Select>
					<FormHelperText>
						{error
							? `Failed to load projects: ${error}`
							: freePlan
							? "System Free Tier workspace for Preliminary tests."
							: projects?.length === 0
							? "You have no projects yet — create one to continue."
							: "Every soil sample must belong to a project."}
					</FormHelperText>
				</FormControl>
				{allowNewProject ? (
					<Button
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={() => setDialogOpen(true)}
						sx={{ mt: { sm: 1 }, whiteSpace: "nowrap", alignSelf: { sm: "flex-start" } }}
					>
						New
					</Button>
				) : null}
			</Stack>
			{error ? (
				<Alert
					severity="error"
					sx={{ mt: 1 }}
					action={
						<Button color="inherit" size="small" onClick={() => setRetryKey((key) => key + 1)}>
							Retry
						</Button>
					}
				>
					Project selection is unavailable.
				</Alert>
			) : null}
			{allowNewProject ? (
				<NewProjectDialog
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					onCreated={handleCreated}
				/>
			) : null}
		</>
	);
}
